import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import { type Project } from '../../types/project';
import { useStyles } from './PublicProjectInformation.styles';
import LabelWithIcon from './LabelWithIcon';
import PublicProjectDataPoint from './PublicProjectDataPoint';
import ExpandableText from '../Layout/ExpandableText';
import { urlProtocolCheck } from '../../helpers/url-protocol-check';
import { formatDatetime } from '../../helpers/format-datetime';
import HeadsIcon from '../Icons/HeadsIcon';
import BuildingIcon from '../Icons/BuildingIcon';
import DocumentIcon from '../Icons/DocumentIcon';

const PublicProjectInformation = ({ project }: { project: Project }) => {
  const I18n = useI18nContext();
  const classes = useStyles();

  const hasPrebidMeetingInfo = !!(
    project.prebidMeeting.date ||
    project.prebidMeeting.location ||
    project.prebidMeeting.onlineLink ||
    project.prebidMeeting.notes
  );

  const hasSiteWalkthroughInfo = !!(
    project.siteWalkthrough.date || project.siteWalkthrough.notes
  );

  const hasBlindBiddingInfo = !!(
    project.blindBidding.enabled &&
    (project.blindBidding.publicBidOpeningDetails.date ||
      project.blindBidding.publicBidOpeningDetails.address ||
      project.blindBidding.publicBidOpeningDetails.onlineLink)
  );

  if (
    !hasPrebidMeetingInfo &&
    !hasSiteWalkthroughInfo &&
    !hasBlindBiddingInfo
  ) {
    return null;
  }

  return (
    <Box
      data-test-id="public-project-information-contents"
      style={{ paddingBottom: '17px' }}
    >
      <Typography variant="h2" className={classes.title}>
        {I18n.t(
          'pages.j.public_project_layout.public_project_information.bid_information'
        )}
      </Typography>
      {hasPrebidMeetingInfo && (
        <div>
          <div style={{ marginLeft: '5px' }}>
            <LabelWithIcon
              label={I18n.t(
                'pages.j.public_project_layout.public_project_information.pre_bid_meeting'
              )}
              icon={<HeadsIcon viewBox="0 2 24 12" />}
            />
          </div>
          <Grid container style={{ marginLeft: '27px' }}>
            {project.prebidMeeting.date && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px', paddingRight: '20px' }}
              >
                <PublicProjectDataPoint
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.pre_bid_meeting_datetime'
                  )}
                  text={formatDatetime(project.prebidMeeting.date)}
                />
              </Grid>
            )}
            {project.prebidMeeting.location && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px', paddingRight: '20px' }}
              >
                <PublicProjectDataPoint
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.pre_bid_meeting_address'
                  )}
                  text={project.prebidMeeting.location}
                />
              </Grid>
            )}
            {project.prebidMeeting.onlineLink && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px' }}
              >
                <LabelWithIcon
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.pre_bid_meeting_online_meeting'
                  )}
                />
                <a
                  className={classes.link}
                  href={urlProtocolCheck(project.prebidMeeting.onlineLink)}
                >
                  {I18n.t(
                    'pages.j.public_project_layout.public_project_information.pre_bid_meeting_join_meeting'
                  )}
                </a>
              </Grid>
            )}
          </Grid>
          {project.prebidMeeting.notes && (
            <ExpandableText
              text={project.prebidMeeting.notes}
              textClassName={classes.notes}
              expandTextClassName={classes.showMoreOrLessText}
              textCutoffLength={400}
            />
          )}
        </div>
      )}
      {hasSiteWalkthroughInfo && (
        <div style={{ marginTop: '25px', marginBottom: '20px' }}>
          <div style={{ marginLeft: '5px' }}>
            <LabelWithIcon
              label={I18n.t(
                'pages.j.public_project_layout.public_project_information.site_walkthrough'
              )}
              icon={<BuildingIcon viewBox="3 -1 30 30" />}
              labelStyle={{ marginLeft: '-2px' }}
            />
          </div>
          {project.siteWalkthrough.date && (
            <Grid container style={{ marginLeft: '27px' }}>
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                style={{ marginBottom: '15px' }}
              >
                <PublicProjectDataPoint
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.site_walkthrough_datetime'
                  )}
                  text={formatDatetime(project.siteWalkthrough.date)}
                />
              </Grid>
            </Grid>
          )}
          {project.siteWalkthrough.notes && (
            <ExpandableText
              text={project.siteWalkthrough.notes}
              textClassName={classes.notes}
              expandTextClassName={classes.showMoreOrLessText}
              textCutoffLength={400}
            />
          )}
        </div>
      )}
      {hasBlindBiddingInfo && (
        <div style={{ marginTop: '25px' }}>
          <div style={{ marginLeft: '5px' }}>
            <LabelWithIcon
              label={I18n.t(
                'pages.j.public_project_layout.public_project_information.blind_bidding'
              )}
              icon={<DocumentIcon viewBox="-3 3 22 12" />}
              labelStyle={{ marginLeft: '0px' }}
            />
          </div>
          <Grid container style={{ marginLeft: '27px' }}>
            {project.blindBidding.publicBidOpeningDetails.date && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px', paddingRight: '20px' }}
              >
                <PublicProjectDataPoint
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.blind_bidding_datetime'
                  )}
                  text={formatDatetime(
                    project.blindBidding.publicBidOpeningDetails.date
                  )}
                />
              </Grid>
            )}
            {project.blindBidding.publicBidOpeningDetails.address && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px', paddingRight: '20px' }}
              >
                <PublicProjectDataPoint
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.blind_bidding_address'
                  )}
                  text={project.blindBidding.publicBidOpeningDetails.address}
                />
              </Grid>
            )}
            {project.blindBidding.publicBidOpeningDetails.onlineLink && (
              <Grid
                className={classes.dataPointContainer}
                item
                xs={12}
                sm={6}
                md={4}
                lg={2.8}
                style={{ marginBottom: '15px' }}
              >
                <LabelWithIcon
                  label={I18n.t(
                    'pages.j.public_project_layout.public_project_information.blind_bidding_online_meeting'
                  )}
                />
                <a
                  className={classes.link}
                  href={urlProtocolCheck(
                    project.blindBidding.publicBidOpeningDetails.onlineLink
                  )}
                >
                  {I18n.t(
                    'pages.j.public_project_layout.public_project_information.pre_bid_meeting_join_meeting'
                  )}
                </a>
              </Grid>
            )}
          </Grid>
        </div>
      )}
    </Box>
  );
};

export default PublicProjectInformation;
